import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { sanitizeDocIdSync, validateFirestorePath } from '@/utils/pmt2PathUtils';
import { getTargetCountData, TargetCountEntry } from '@/services/pmt2TargetCountService';
import { getSevenDayCampaignResultsData, SevenDayCampaignData, SevenDayCampaignResult } from '@/services/pmt2SevenDayCampaignResultsService';
import { getMonthlyCampaignResultsData, MonthlyCampaignData, MonthlyCampaignResult } from '@/services/pmt2MonthlyCampaignResultsService';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

interface CampaignsDashboardProps {
  product: PMT2Product | null;
}

type DataPeriod = '7-day' | '30-day';
type OldValuesMap = Record<string, string>;

interface CampaignRow {
  name: string;
  statusField: string;
  bidType: 'max' | 'default' | 'ca-auto' | 'micro-auto';
  targetCountField?: keyof TargetCountEntry; // Add optional field for target count mapping
}

interface LedgerEntry {
  Date: string;
  'Data Element': string;
  'New Value': string;
  ASIN: string;
}

// Campaign configuration based on the screenshot order with target count field mapping
// Moved outside component to prevent re-creation on every render
const campaignRows: CampaignRow[] = [
  { name: 'Exact', statusField: 'Exact Status', bidType: 'max', targetCountField: 'exact' },
  { name: 'TOS Exact', statusField: 'TOS Exact Status', bidType: 'max', targetCountField: 'tosExact' },
  { name: 'ROS Exact', statusField: 'ROS Exact Status', bidType: 'max', targetCountField: 'rosExact' },
  { name: 'PP Exact', statusField: 'PP Exact Status', bidType: 'max', targetCountField: 'ppExact' },
  { name: 'PTA', statusField: 'PTA Status', bidType: 'max', targetCountField: 'pta' },
  { name: 'TOS PTA', statusField: 'TOS PTA Status', bidType: 'max', targetCountField: 'tosPTA' },
  { name: 'ROS PTA', statusField: 'ROS PTA Status', bidType: 'max', targetCountField: 'rosPTA' },
  { name: 'PP PTA', statusField: 'PP PTA Status', bidType: 'max', targetCountField: 'ppPTA' },
  { name: 'Broad', statusField: 'Broad Status', bidType: 'default', targetCountField: 'broad' },
  { name: 'CTA', statusField: 'CTA Status', bidType: 'default', targetCountField: 'cta' },
  { name: 'Full Auto', statusField: 'Full Auto Status', bidType: 'default' },
  { name: 'Full Auto Close-Match', statusField: 'Full Auto Close-Match Status', bidType: 'default' },
  { name: 'Full Auto Complements', statusField: 'Full Auto Complements Status', bidType: 'default' },
  { name: 'Full Auto Loose-Match', statusField: 'Full Auto Loose-Match Status', bidType: 'default' },
  { name: 'Full Auto Substitutes', statusField: 'Full Auto Substitutes Status', bidType: 'default' },
  { name: 'CA Auto', statusField: 'CA Auto Status', bidType: 'ca-auto' },
  { name: 'CA Auto Close-Match', statusField: 'CA Auto Close-Match Status', bidType: 'ca-auto' },
  { name: 'CA Auto Complements', statusField: 'CA Auto Complements Status', bidType: 'ca-auto' },
  { name: 'CA Auto Loose-Match', statusField: 'CA Auto Loose-Match Status', bidType: 'ca-auto' },
  { name: 'CA Auto Substitutes', statusField: 'CA Auto Substitutes Status', bidType: 'ca-auto' },
  { name: 'Micro Auto', statusField: 'Micro Auto Status', bidType: 'micro-auto' },
  { name: 'Micro Auto Close-Match', statusField: 'Micro Auto Close-Match Status', bidType: 'micro-auto' },
  { name: 'Micro Auto Complements', statusField: 'Micro Auto Complements Status', bidType: 'micro-auto' },
  { name: 'Micro Auto Loose-Match', statusField: 'Micro Auto Loose-Match Status', bidType: 'micro-auto' },
  { name: 'Micro Auto Substitutes', statusField: 'Micro Auto Substitutes Status', bidType: 'micro-auto' },
];

const CampaignsDashboard: React.FC<CampaignsDashboardProps> = ({ product }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<DataPeriod>('7-day');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ledgerData, setLedgerData] = useState<LedgerEntry[]>([]);
  const [targetCountData, setTargetCountData] = useState<TargetCountEntry | null>(null);
  const [campaignResultsData, setCampaignResultsData] = useState<SevenDayCampaignData | null>(null);
  const [monthlyCampaignResultsData, setMonthlyCampaignResultsData] = useState<MonthlyCampaignData | null>(null);
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [overrideData, setOverrideData] = useState<Record<string, any> | null>(null);
  const [overrideDatesData, setOverrideDatesData] = useState<Record<string, any> | null>(null);

  // Fetch ledger data, target count data, and campaign results data
  useEffect(() => {
    const fetchData = async () => {
      if (!product?.companyName || !product?.id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch ledger data, target count data, both campaign results data, old values data, override data, and override dates in parallel
        const [ledgerResult, targetCountResult, campaignResultsResult, monthlyCampaignResultsResult, oldValuesResult, overrideResult, overrideDatesResult] = await Promise.all([
          // Fetch ledger data
          (async () => {
            const statusFields = campaignRows.map(row => row.statusField);
            const allLedgerData: LedgerEntry[] = [];

            // Use Promise.all to fetch all status documents in parallel for better performance
            const fetchPromises = statusFields.map(async (statusField) => {
              try {
                // Sanitize path components to prevent path length errors
                const sanitizedAsin = sanitizeDocIdSync(product.id, {
                  maxLength: 100,
                  context: 'campaigns-asin'
                });
                const sanitizedStatusField = sanitizeDocIdSync(statusField, {
                  maxLength: 100,
                  context: 'campaigns-statusField'
                });
                
                // Validate the complete path
                const fullPath = `pmt2_ledger/${product.companyName}/${sanitizedAsin}/${sanitizedStatusField}`;
                const pathValidation = validateFirestorePath(fullPath);
                
                if (!pathValidation.valid) {
                  console.warn(`[CampaignsDashboard] Path validation failed for ${statusField}: ${pathValidation.message}`);
                }
                
                const docRef = doc(db, 'pmt2_ledger', product.companyName, sanitizedAsin, sanitizedStatusField);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                  const data = docSnap.data();
                  
                  // Check if entries array exists
                  if (Array.isArray(data?.entries)) {
                    // Process each entry in the array
                    return data.entries.map((entry: any) => ({
                      Date: entry.Date || entry.date?.toDate?.()?.toLocaleDateString?.() || '',
                      'Data Element': statusField,
                      'New Value': entry['New Value'] || entry.newValue || '',
                      ASIN: entry.ASIN || product.id
                    }));
                  }
                }
                return [];
              } catch (error) {
                console.error(`Error fetching ledger data for ${statusField}:`, error);
                return [];
              }
            });

            // Wait for all queries to complete in parallel
            const results = await Promise.all(fetchPromises);
            
            // Flatten the results into allLedgerData
            results.forEach(entries => {
              allLedgerData.push(...entries);
            });

            return allLedgerData;
          })(),
          
          // Fetch target count data
          getTargetCountData(product.companyName, product.id),
          
          // Fetch campaign results data
          getSevenDayCampaignResultsData(product.companyName, product.id),
          
          // Fetch monthly campaign results data
          getMonthlyCampaignResultsData(product.companyName, product.id),
          
          // Fetch old values data
          fetchLatestOldValuesMap(product.companyName, product.id),
          
          // Fetch override data from pmt2_data collection
          (async () => {
            try {
              const overrideDocRef = doc(db, `pmt2_data/${product.companyName}/products/${product.id}`);
              const overrideDocSnap = await getDoc(overrideDocRef);
              return overrideDocSnap.exists() ? overrideDocSnap.data() : null;
            } catch (error) {
              console.error('Error fetching override data:', error);
              return null;
            }
          })(),
          
          // Fetch override dates from pmt2_ledger collection
          (async () => {
            const overrideDateFields = [
              'Exact Campaign Max Bid Override',
              'TOS Exact Campaign Max Bid Override',
              'ROS Exact Campaign Max Bid Override',
              'PP Exact Campaign Max Bid Override',
              'PTA Campaign Max Bid Override',
              'TOS PTA Campaign Max Bid Override',
              'ROS PTA Campaign Max Bid Override',
              'PP PTA Campaign Max Bid Override',
              'Broad Campaign Max Bid Override',
              'CTA Campaign Max Bid Override',
              'Full Auto Campaign Max Bid Override',
              'Full Auto Close-Match Bid Override',
              'Full Auto Complements Bid Override',
              'Full Auto Loose-Match Bid Override',
              'Full Auto Substitutes Bid Override',
              'CA Auto Campaign Max Bid Override',
              'CA Auto Close-Match Bid Override',
              'CA Auto Complements Bid Override',
              'CA Auto Loose-Match Bid Override',
              'CA Auto Substitutes Bid Override',
              'Micro Auto Campaign Max Bid Override',
              'Micro Auto Close-Match Bid Override',
              'Micro Auto Complements Bid Override',
              'Micro Auto Loose-Match Bid Override',
              'Micro Auto Substitutes Bid Override'
            ];
            
            const overrideDates: Record<string, number | null> = {};
            
            // Fetch all override documents in parallel
            const datePromises = overrideDateFields.map(async (fieldName) => {
              try {
                // Sanitize path components for override dates
                const sanitizedAsin = sanitizeDocIdSync(product.id, {
                  maxLength: 100,
                  context: 'campaigns-override-asin'
                });
                const sanitizedFieldName = sanitizeDocIdSync(fieldName, {
                  maxLength: 100,
                  context: 'campaigns-override-field'
                });
                
                // Validate the complete path
                const fullPath = `pmt2_ledger/${product.companyName}/${sanitizedAsin}/${sanitizedFieldName}`;
                const pathValidation = validateFirestorePath(fullPath);
                
                if (!pathValidation.valid) {
                  console.warn(`[CampaignsDashboard] Override path validation failed for ${fieldName}: ${pathValidation.message}`);
                }
                
                const docRef = doc(db, 'pmt2_ledger', product.companyName, sanitizedAsin, sanitizedFieldName);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                  const data = docSnap.data();
                  if (data.entries && Array.isArray(data.entries) && data.entries.length > 0) {
                    // Find the most recent entry by date
                    const sortedEntries = data.entries
                      .filter(entry => entry.Date || entry.date)
                      .sort((a, b) => {
                        const dateA = new Date(a.Date || a.date);
                        const dateB = new Date(b.Date || b.date);
                        return dateB.getTime() - dateA.getTime();
                      });
                    
                    if (sortedEntries.length > 0) {
                      const mostRecentDate = new Date(sortedEntries[0].Date || sortedEntries[0].date);
                      const currentDate = new Date();
                      const diffTime = currentDate.getTime() - mostRecentDate.getTime();
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return { fieldName, days: diffDays >= 0 ? diffDays : null };
                    }
                  }
                }
                return { fieldName, days: null };
              } catch (error) {
                console.error(`Error fetching override date for ${fieldName}:`, error);
                return { fieldName, days: null };
              }
            });
            
            const results = await Promise.all(datePromises);
            results.forEach(result => {
              overrideDates[result.fieldName] = result.days;
            });
            
            return overrideDates;
          })()
        ]);

        setLedgerData(ledgerResult);
        setTargetCountData(targetCountResult.targetCounts[0] || null);
        setCampaignResultsData(campaignResultsResult);
        setMonthlyCampaignResultsData(monthlyCampaignResultsResult);
        setOldValuesMap(oldValuesResult);
        setOverrideData(overrideResult);
        setOverrideDatesData(overrideDatesResult);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [product, selectedPeriod]);

  // Helper functions
  const formatCurrency = (value: any): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toFixed(2)}`;
  };

  const formatStatus = (value: any): string => {
    if (!value || value === '') return '-';
    return String(value);
  };

  const getDefaultMaxBid = (bidType: string): string => {
    if (!product) return '-';

    switch (bidType) {
      case 'max':
        return formatCurrency(product['Max Bid'] || 0);
      case 'default':
        return formatCurrency(product['Default Bid'] || 0);
      case 'ca-auto':
        return '$0.20';
      case 'micro-auto':
        return '$0.05';
      default:
        return '-';
    }
  };

  const getLastChange = (statusField: string): { date: string; daysAgo: string } => {
    const entries = ledgerData.filter(entry => entry['Data Element'] === statusField);
    if (entries.length === 0) return { date: '-', daysAgo: '' };

    // Sort by date descending to get the most recent
    const sortedEntries = entries.sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime());
    const mostRecent = sortedEntries[0];

    if (!mostRecent) return { date: '-', daysAgo: '' };

    const entryDate = new Date(mostRecent.Date);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - entryDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Format date as MM/DD/YY
    const formattedDate = entryDate.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });

    return {
      date: formattedDate,
      daysAgo: `(${diffDays.toLocaleString()} days ago)`
    };
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'enabled') {
      return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
    } else if (statusLower === 'paused') {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100';
    }
    return '';
  };

  const getCampaignRowColor = (campaignName: string): string => {
    // Add background colors for different campaign groups like in the sheets
    if (campaignName.includes('Exact') || campaignName.includes('PTA')) {
      return 'bg-blue-50 dark:bg-blue-900/20';
    } else if (campaignName.includes('Broad') || campaignName.includes('CTA') || campaignName.includes('Full Auto')) {
      return 'bg-green-50 dark:bg-green-900/20';
    } else if (campaignName.includes('CA Auto')) {
      return 'bg-purple-50 dark:bg-purple-900/20';
    } else if (campaignName.includes('Micro Auto')) {
      return 'bg-amber-50 dark:bg-amber-900/20'; // Changed from orange to amber for subtlety
    }
    return '';
  };

  // Helper to get solid background color for sticky column (no transparency)
  const getStickyColumnColor = (campaignName: string): string => {
    if (campaignName.includes('Exact') || campaignName.includes('PTA')) {
      return 'bg-blue-50 dark:bg-blue-900';
    } else if (campaignName.includes('Broad') || campaignName.includes('CTA') || campaignName.includes('Full Auto')) {
      return 'bg-green-50 dark:bg-green-900';
    } else if (campaignName.includes('CA Auto')) {
      return 'bg-purple-50 dark:bg-purple-900';
    } else if (campaignName.includes('Micro Auto')) {
      return 'bg-amber-50 dark:bg-amber-900';
    }
    return 'bg-white dark:bg-gray-900';
  };

  // Helper to determine if we need a separator row after this campaign
  const needsSeparatorAfter = (index: number): boolean => {
    if (index >= campaignRows.length - 1) return false;
    
    const currentCampaign = campaignRows[index];
    const nextCampaign = campaignRows[index + 1];
    
    // Add separator between different campaign groups
    const getCurrentGroup = (name: string) => {
      if (name.includes('Exact') || name.includes('PTA')) return 'exact-pta';
      if (name.includes('Broad') || name.includes('CTA') || name.includes('Full Auto')) return 'broad-auto';
      if (name.includes('CA Auto')) return 'ca-auto';
      if (name.includes('Micro Auto')) return 'micro-auto';
      return 'other';
    };
    
    return getCurrentGroup(currentCampaign.name) !== getCurrentGroup(nextCampaign.name);
  };

  const getGlobalMaxBid = (): string => {
    if (!product) return '-';
    return formatCurrency(product['Max Bid'] || 0);
  };

  // Helper function to get target count for a campaign
  const getTargetCount = (campaign: CampaignRow): string => {
    if (!targetCountData || !campaign.targetCountField) return '-';
    
    const count = targetCountData[campaign.targetCountField];
    if (count === null || count === undefined) return '-';
    
    return count.toString();
  };

  // Helper function to get campaign result data for a specific campaign
  const getCampaignResult = (campaignName: string): SevenDayCampaignResult | MonthlyCampaignResult | null => {
    // Use the appropriate data source based on selected period
    const currentData = selectedPeriod === '7-day' ? campaignResultsData : monthlyCampaignResultsData;
    if (!currentData?.campaigns) return null;
    
    // Handle aggregated campaigns (Full Auto, CA Auto, Micro Auto)
    if (campaignName === 'Full Auto') {
      const subCampaigns = ['Full Auto Close-Match', 'Full Auto Complements', 'Full Auto Loose-Match', 'Full Auto Substitutes'];
      return aggregateCampaignResults(subCampaigns);
    } else if (campaignName === 'CA Auto') {
      const subCampaigns = ['CA Auto Close-Match', 'CA Auto Complements', 'CA Auto Loose-Match', 'CA Auto Substitutes'];
      return aggregateCampaignResults(subCampaigns);
    } else if (campaignName === 'Micro Auto') {
      const subCampaigns = ['Micro Auto Close-Match', 'Micro Auto Complements', 'Micro Auto Loose-Match', 'Micro Auto Substitutes'];
      return aggregateCampaignResults(subCampaigns);
    }
    
    // Find exact match for individual campaigns
    return currentData.campaigns.find(c => c.campaignType === campaignName) || null;
  };

  // Helper function to aggregate campaign results for parent campaigns
  const aggregateCampaignResults = (subCampaignNames: string[]): SevenDayCampaignResult | MonthlyCampaignResult | null => {
    // Use the appropriate data source based on selected period
    const currentData = selectedPeriod === '7-day' ? campaignResultsData : monthlyCampaignResultsData;
    if (!currentData?.campaigns) return null;
    
    const subCampaigns = currentData.campaigns.filter(c =>
      subCampaignNames.includes(c.campaignType)
    );
    
    if (subCampaigns.length === 0) return null;
    
    return subCampaigns.reduce((acc, campaign) => ({
      uniqueCode: 'AGGREGATED',
      campaignType: 'AGGREGATED',
      impressions: acc.impressions + campaign.impressions,
      clicks: acc.clicks + campaign.clicks,
      spend: acc.spend + campaign.spend,
      sales: acc.sales + campaign.sales,
      orders: acc.orders + campaign.orders,
      units: acc.units + campaign.units
    }), {
      uniqueCode: 'AGGREGATED',
      campaignType: 'AGGREGATED',
      impressions: 0,
      clicks: 0,
      spend: 0,
      sales: 0,
      orders: 0,
      units: 0
    });
  };

  // Helper functions to format campaign data
  const formatClicks = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    return result && result.clicks > 0 ? result.clicks.toLocaleString() : '-';
  };

  const formatSpend = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    return result && result.spend > 0 ? `$${Math.round(result.spend).toLocaleString()}` : '-';
  };

  const formatSales = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    return result && result.sales > 0 ? `$${Math.round(result.sales).toLocaleString()}` : '-';
  };

  const formatUnits = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    return result && result.units > 0 ? result.units.toLocaleString() : '-';
  };

  const formatACoS = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    if (!result || result.spend <= 0 || result.sales <= 0) return '-';
    
    const acos = (result.spend / result.sales) * 100;
    return `${Math.round(acos)}%`;
  };

  const getACoSColor = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    if (!result || result.spend <= 0 || result.sales <= 0 || !product) return '';
    
    const acos = (result.spend / result.sales) * 100;
    const finalTargetACoS = product['Final Target ACoS'];
    
    if (!finalTargetACoS || finalTargetACoS <= 0) return '';
    
    // Convert Final Target ACoS to percentage and multiply by 1.05
    const threshold = (finalTargetACoS * 100) * 1.05;
    
    if (acos > threshold) {
      return 'text-red-600 dark:text-red-400 font-semibold'; // Red for above threshold
    } else {
      return 'text-green-600 dark:text-green-400 font-semibold'; // Green for at or below threshold
    }
  };

  const formatCPC = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    if (!result || result.spend <= 0 || result.clicks <= 0) return '-';
    
    const cpc = result.spend / result.clicks;
    return `$${cpc.toFixed(2)}`;
  };

  // Helper function to calculate totals for summary row
  const calculateTotals = () => {
    const summaryRows = [
      'Exact', 'TOS Exact', 'ROS Exact', 'PP Exact',
      'PTA', 'TOS PTA', 'ROS PTA', 'PP PTA',
      'Broad', 'CTA', 'Full Auto', 'CA Auto', 'Micro Auto'
    ];

    let totalTargets = 0;
    let totalClicks = 0;
    let totalSpend = 0;
    let totalSales = 0;
    let totalUnits = 0;

    summaryRows.forEach(campaignName => {
      const campaign = campaignRows.find(c => c.name === campaignName);
      if (campaign && targetCountData && campaign.targetCountField) {
        const count = targetCountData[campaign.targetCountField];
        if (count && typeof count === 'number' && count > 0) {
          totalTargets += count;
        }
      }

      const result = getCampaignResult(campaignName);
      if (result) {
        totalClicks += result.clicks;
        totalSpend += result.spend;
        totalSales += result.sales;
        totalUnits += result.units;
      }
    });

    return { totalTargets, totalClicks, totalSpend, totalSales, totalUnits };
  };

  const formatTotalACoS = (): { value: string; color: string } => {
    const { totalSpend, totalSales } = calculateTotals();
    if (totalSpend <= 0 || totalSales <= 0) return { value: '-', color: '' };
    
    const acos = (totalSpend / totalSales) * 100;
    const finalTargetACoS = product?.['Final Target ACoS'];
    
    let color = '';
    if (finalTargetACoS && finalTargetACoS > 0) {
      const threshold = (finalTargetACoS * 100) * 1.05;
      if (acos > threshold) {
        color = 'text-red-600 dark:text-red-400 font-semibold';
      } else {
        color = 'text-green-600 dark:text-green-400 font-semibold';
      }
    }
    
    return { value: `${Math.round(acos)}%`, color };
  };

  const formatTotalCPC = (): string => {
    const { totalSpend, totalClicks } = calculateTotals();
    if (totalSpend <= 0 || totalClicks <= 0) return '-';
    
    const cpc = totalSpend / totalClicks;
    return `$${cpc.toFixed(2)}`;
  };

  // Helper functions for old values
  const getOldStatus = (campaignName: string): string => {
    if (!oldValuesMap) return '-';
    
    // Generate the old status key based on campaign name
    // Examples: 'ExactStatus', 'ROS ExactStatus', 'PP ExactStatus'
    let statusKey = '';
    if (campaignName.includes('ROS ')) {
      statusKey = `ROS ${campaignName.replace('ROS ', '')}Status`;
    } else if (campaignName.includes('TOS ')) {
      statusKey = `TOS ${campaignName.replace('TOS ', '')}Status`;
    } else if (campaignName.includes('PP ')) {
      statusKey = `PP ${campaignName.replace('PP ', '')}Status`;
    } else {
      statusKey = `${campaignName}Status`;
    }
    
    const oldValue = oldValuesMap[statusKey];
    return oldValue || '-';
  };

  const getOldOverride = (campaignName: string): string => {
    if (!oldValuesMap) return '-';
    
    // Generate the old override key based on campaign name
    // Examples: 'ExactMax Bid Override', 'ROSMax Bid Override', 'PPMax Bid Override'
    let overrideKey = '';
    if (campaignName.includes('ROS ')) {
      overrideKey = `ROS${campaignName.replace('ROS ', '')}Max Bid Override`;
    } else if (campaignName.includes('TOS ')) {
      overrideKey = `TOS${campaignName.replace('TOS ', '')}Max Bid Override`;
    } else if (campaignName.includes('PP ')) {
      overrideKey = `PP${campaignName.replace('PP ', '')}Max Bid Override`;
    } else {
      overrideKey = `${campaignName}Max Bid Override`;
    }
    
    const oldValue = oldValuesMap[overrideKey];
    return oldValue || '-';
  };

  const getOldTargets = (campaignName: string): string => {
    if (!oldValuesMap) return '-';
    
    // Generate the old targets key based on campaign name
    // Examples: 'ExactTarget', 'ROSTarget', 'PPTarget'
    let targetsKey = '';
    if (campaignName.includes('ROS ')) {
      targetsKey = `ROS${campaignName.replace('ROS ', '')}Target`;
    } else if (campaignName.includes('TOS ')) {
      targetsKey = `TOS${campaignName.replace('TOS ', '')}Target`;
    } else if (campaignName.includes('PP ')) {
      targetsKey = `PP${campaignName.replace('PP ', '')}Target`;
    } else {
      targetsKey = `${campaignName}Target`;
    }
    
    const oldValue = oldValuesMap[targetsKey];
    return oldValue || '-';
  };

  // Helper function to check if old value should be highlighted (different from current)
  const shouldHighlightOldValue = (currentValue: string, oldValue: string): boolean => {
    return oldValue !== '-' && oldValue !== currentValue;
  };

  // Helper function to get Max Bid Override value for a campaign
  const getMaxBidOverride = (campaignName: string): string => {
    if (!overrideData) return '-';
    
    // Map campaign names to their corresponding field names in pmt2_data
    const fieldMapping: Record<string, string> = {
      'Exact': 'Exact Campaign Max Bid Override',
      'TOS Exact': 'TOS Exact Campaign Max Bid Override',
      'ROS Exact': 'ROS Exact Campaign Max Bid Override',
      'PP Exact': 'PP Exact Campaign Max Bid Override',
      'PTA': 'PTA Campaign Max Bid Override',
      'TOS PTA': 'TOS PTA Campaign Max Bid Override',
      'ROS PTA': 'ROS PTA Campaign Max Bid Override',
      'PP PTA': 'PP PTA Campaign Max Bid Override',
      'Broad': 'Broad Override Bid',
      'CTA': 'CTA Override Bid',
      'Full Auto': 'Full Auto Override Bid',
      'Full Auto Close-Match': 'Full Auto Close-Match Bid Override',
      'Full Auto Complements': 'Full Auto Complements Bid Override',
      'Full Auto Loose-Match': 'Full Auto Loose-Match Bid Override',
      'Full Auto Substitutes': 'Full Auto Substitutes Bid Override',
      'CA Auto': 'CA Auto Bid Override',
      'CA Auto Close-Match': 'CA Auto Close-Match Bid Override',
      'CA Auto Complements': 'CA Auto Complements Bid Override',
      'CA Auto Loose-Match': 'CA Auto Loose-Match Bid Override',
      'CA Auto Substitutes': 'CA Auto Substitutes Bid Override',
      'Micro Auto': 'Micro Auto Bid Override',
      'Micro Auto Close-Match': 'Micro Auto Close-Match Bid Override',
      'Micro Auto Complements': 'Micro Auto Complements Bid Override',
      'Micro Auto Loose-Match': 'Micro Auto Loose-Match Bid Override',
      'Micro Auto Substitutes': 'Micro Auto Substitutes Bid Override'
    };
    
    const fieldName = fieldMapping[campaignName];
    if (!fieldName) return '-';
    
    const value = overrideData[fieldName];
    if (!value || value === '' || value === '-') return '-';
    
    // Format as currency if it's a number
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return `$${numValue.toFixed(2)}`;
    }
    
    return String(value);
  };

  // Helper function to get Override Days Ago for a campaign
  const getOverrideDaysAgo = (campaignName: string): string => {
    if (!overrideDatesData) return '-';
    
    // Map campaign names to their corresponding ledger document names
    const ledgerDocMapping: Record<string, string> = {
      'Exact': 'Exact Campaign Max Bid Override',
      'TOS Exact': 'TOS Exact Campaign Max Bid Override',
      'ROS Exact': 'ROS Exact Campaign Max Bid Override',
      'PP Exact': 'PP Exact Campaign Max Bid Override',
      'PTA': 'PTA Campaign Max Bid Override',
      'TOS PTA': 'TOS PTA Campaign Max Bid Override',
      'ROS PTA': 'ROS PTA Campaign Max Bid Override',
      'PP PTA': 'PP PTA Campaign Max Bid Override',
      'Broad': 'Broad Campaign Max Bid Override',
      'CTA': 'CTA Campaign Max Bid Override',
      'Full Auto': 'Full Auto Campaign Max Bid Override',
      'Full Auto Close-Match': 'Full Auto Close-Match Bid Override',
      'Full Auto Complements': 'Full Auto Complements Bid Override',
      'Full Auto Loose-Match': 'Full Auto Loose-Match Bid Override',
      'Full Auto Substitutes': 'Full Auto Substitutes Bid Override',
      'CA Auto': 'CA Auto Campaign Max Bid Override',
      'CA Auto Close-Match': 'CA Auto Close-Match Bid Override',
      'CA Auto Complements': 'CA Auto Complements Bid Override',
      'CA Auto Loose-Match': 'CA Auto Loose-Match Bid Override',
      'CA Auto Substitutes': 'CA Auto Substitutes Bid Override',
      'Micro Auto': 'Micro Auto Campaign Max Bid Override',
      'Micro Auto Close-Match': 'Micro Auto Close-Match Bid Override',
      'Micro Auto Complements': 'Micro Auto Complements Bid Override',
      'Micro Auto Loose-Match': 'Micro Auto Loose-Match Bid Override',
      'Micro Auto Substitutes': 'Micro Auto Substitutes Bid Override'
    };
    
    const ledgerDocName = ledgerDocMapping[campaignName];
    if (!ledgerDocName) return '-';
    
    const daysAgo = overrideDatesData[ledgerDocName];
    if (daysAgo === null || daysAgo === undefined) return '-';
    
    return `${daysAgo}`;
  };

  if (!product) {
    return (
      <div className="w-full">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500">Select a product to view Campaigns.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Card>
        <CardHeader className="p-4">
          <CardTitle className="text-2xl font-bold">Campaigns</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* Data Period Dropdown and Global Max Bid - inline like spreadsheet */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select value={selectedPeriod} onValueChange={(value: DataPeriod) => setSelectedPeriod(value)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select data period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7-day">Data: 7 Day Results</SelectItem>
                  <SelectItem value="30-day">Data: 30 Day Results</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Global Max Bid</span>
              <span className="font-bold text-lg">{getGlobalMaxBid()}</span>
            </div>
          </div>

          {/* Campaigns Table with Frozen Header/Column */}
          <div className="w-full border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
            <div className="h-[600px] overflow-auto">
              <table className="w-full border-collapse">
                {/* Header row - sticky with compact two-row design */}
                <thead className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="sticky left-0 z-30 bg-gray-100 dark:bg-gray-700 px-2 py-2 text-left text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[120px]">
                      Campaign
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">Status</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">
                      <div>Old</div>
                      <div>Status</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[70px]">
                      <div>Last</div>
                      <div>Change</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">
                      <div>Default</div>
                      <div>Max Bid</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">
                      <div>Max Bid</div>
                      <div>Override</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">
                      <div>Old</div>
                      <div>Override</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">
                      <div>Override</div>
                      <div>Days Ago</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">Targets</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">
                      <div>Old</div>
                      <div>Targets</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">Clicks</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">Spend</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">Sales</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">Units</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">ACoS</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-b min-w-[50px]">CPC</th>
                  </tr>
                </thead>

                {/* Data rows */}
                <tbody>
                  {/* Summary row with totals */}
                  <tr className="bg-blue-100 dark:bg-blue-900/30 font-semibold border-b-2 border-blue-300 dark:border-blue-600">
                    <td className="sticky left-0 z-10 px-2 py-2 text-xs font-bold text-gray-900 dark:text-gray-100 border-r border-b bg-blue-100 dark:bg-blue-900/30 min-w-[120px]">
                      {selectedPeriod === '7-day' ? 'Data: 7 Day Results' : 'Data: 30 Day Results'}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[70px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-8 mx-auto" />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">
                          {calculateTotals().totalTargets > 0 ? calculateTotals().totalTargets.toLocaleString() : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">-</td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-8 mx-auto" />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">
                          {calculateTotals().totalClicks > 0 ? calculateTotals().totalClicks.toLocaleString() : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-12 mx-auto" />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">
                          {calculateTotals().totalSpend > 0 ? `$${Math.round(calculateTotals().totalSpend).toLocaleString()}` : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-12 mx-auto" />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">
                          {calculateTotals().totalSales > 0 ? `$${Math.round(calculateTotals().totalSales).toLocaleString()}` : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-8 mx-auto" />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">
                          {calculateTotals().totalUnits > 0 ? calculateTotals().totalUnits.toLocaleString() : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-8 mx-auto" />
                      ) : (
                        <span className={`${formatTotalACoS().color || 'text-gray-900 dark:text-gray-100'}`}>
                          {formatTotalACoS().value}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2 text-center text-xs border-b min-w-[50px] whitespace-nowrap">
                      {isLoading ? (
                        <Skeleton className="h-4 w-12 mx-auto" />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">
                          {formatTotalCPC()}
                        </span>
                      )}
                    </td>
                  </tr>

                  {campaignRows.map((campaign, index) => {
                    const status = formatStatus(product[campaign.statusField]);
                    const lastChange = getLastChange(campaign.statusField);
                    const defaultMaxBid = getDefaultMaxBid(campaign.bidType);
                    const rowColor = getCampaignRowColor(campaign.name);

                    return (
                      <React.Fragment key={campaign.name}>
                        <tr className={`hover:bg-gray-100 dark:hover:bg-gray-600 ${rowColor}`}>
                          {/* Campaign Name - sticky */}
                          <td className={`sticky left-0 z-10 px-2 py-2 text-xs font-semibold text-gray-900 dark:text-gray-100 border-r border-b min-w-[120px] ${getStickyColumnColor(campaign.name)}`}>
                            {campaign.name}
                          </td>
                          
                          {/* Status - Implemented with color coding */}
                          <td className="px-1 py-2 text-center border-r border-b min-w-[60px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${getStatusColor(status)}`}>
                                {status}
                              </span>
                            )}
                          </td>
                          
                          {/* Old Status - Implemented with old values data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              (() => {
                                const oldStatus = getOldStatus(campaign.name);
                                const shouldHighlight = shouldHighlightOldValue(status, oldStatus);
                                return shouldHighlight ? (
                                  <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100 text-[10px]">
                                    {oldStatus}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                );
                              })()
                            )}
                          </td>
                          
                          {/* Last Change - Implemented with compact two-row format */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[70px]">
                            {isLoading ? (
                              <Skeleton className="h-8 w-16 mx-auto" />
                            ) : (
                              <div className="flex flex-col">
                                <div className="text-gray-900 dark:text-gray-100">{lastChange.date}</div>
                                <div className="text-gray-500 dark:text-gray-400 text-[10px]">{lastChange.daysAgo}</div>
                              </div>
                            )}
                          </td>
                          
                          {/* Default Max Bid - Implemented */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">
                            {isLoading ? <Skeleton className="h-4 w-12 mx-auto" /> : defaultMaxBid}
                          </td>
                          
                          {/* Max Bid Override - Implemented with pmt2_data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {getMaxBidOverride(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* Old Override - Implemented with old values data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              (() => {
                                const currentOverride = getMaxBidOverride(campaign.name);
                                const oldOverride = getOldOverride(campaign.name);
                                const shouldHighlight = shouldHighlightOldValue(currentOverride, oldOverride);
                                return shouldHighlight ? (
                                  <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100 text-[10px]">
                                    {oldOverride}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                );
                              })()
                            )}
                          </td>
                          
                          {/* Override Days Ago - Implemented with override dates data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-8 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {getOverrideDaysAgo(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* Targets - Implemented with target count data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-8 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {getTargetCount(campaign)}
                              </span>
                            )}
                          </td>
                          
                          {/* Old Targets - Implemented with old values data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-8 mx-auto" />
                            ) : (
                              (() => {
                                const currentTargets = getTargetCount(campaign);
                                const oldTargets = getOldTargets(campaign.name);
                                const shouldHighlight = shouldHighlightOldValue(currentTargets, oldTargets);
                                return shouldHighlight ? (
                                  <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100 text-[10px]">
                                    {oldTargets}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                );
                              })()
                            )}
                          </td>
                          
                          {/* Clicks - Implemented with campaign results data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-8 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {formatClicks(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* Spend - Implemented with campaign results data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {formatSpend(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* Sales - Implemented with campaign results data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {formatSales(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* Units - Implemented with campaign results data */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-8 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {formatUnits(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* ACoS - Implemented with calculated percentage and color coding */}
                          <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-8 mx-auto" />
                            ) : (
                              <span className={`${getACoSColor(campaign.name) || 'text-gray-900 dark:text-gray-100'}`}>
                                {formatACoS(campaign.name)}
                              </span>
                            )}
                          </td>
                          
                          {/* CPC - Implemented with calculated cost per click */}
                          <td className="px-1 py-2 text-center text-xs border-b min-w-[50px] whitespace-nowrap">
                            {isLoading ? (
                              <Skeleton className="h-4 w-12 mx-auto" />
                            ) : (
                              <span className="text-gray-900 dark:text-gray-100">
                                {formatCPC(campaign.name)}
                              </span>
                            )}
                          </td>
                        </tr>
                        
                        {/* Add separator row between different campaign groups */}
                        {needsSeparatorAfter(index) && (
                          <tr className="bg-gray-200 dark:bg-gray-600">
                            <td colSpan={16} className="h-2 border-b"></td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignsDashboard;